import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Star, ArrowRight } from "lucide-react";
import { brand, technology } from "../mock/content";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { t } from "../lib/ui";
import { getActiveLang } from "../lib/activeLang";

// Overlay recensioni Google (in alto a destra, su tutte le slide).
// Riusa i dati reali già presenti in /place-reviews.json (rigenerato al build).
function ReviewsOverlay() {
  const [data, setData] = useState(null);
  const [ri, setRi] = useState(0);

  useEffect(() => {
    const lang = getActiveLang();
    const urls = [`/place-reviews.${lang}.json`, "/place-reviews.json"];
    (async () => {
      for (const u of urls) {
        try {
          const r = await fetch(u, { cache: "no-store" });
          if (r.ok) {
            const d = await r.json();
            if (d && d.rating) { setData(d); return; }
          }
        } catch (e) { /* prova il prossimo */ }
      }
      setData(null);
    })();
  }, []);

  const reviews = data?.reviews ?? [];
  useEffect(() => {
    if (reviews.length < 2) return;
    const t = setInterval(() => setRi((p) => (p + 1) % reviews.length), 5000);
    return () => clearInterval(t);
  }, [reviews.length]);

  if (!data) return null;
  const r = reviews[ri];

  return (
    <a
      href={data.googleMapsUri || "#"}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="campaign-reviews-overlay"
      className="absolute z-20 top-20 right-3 sm:top-16 sm:right-20 lg:right-40 w-[210px] sm:w-[248px] block group"
    >
      <div
        className="p-3.5 sm:p-4 rounded-2xl border border-white/15 shadow-2xl transition-all duration-300 group-hover:border-white/30"
        style={{ background: "rgba(0,0,0,0.42)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)" }}
      >
        {/* aggregato */}
        <div className="flex items-center gap-2.5">
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.2 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.5 4.5 29.5 2 24 2 12 2 2 12 2 24s10 22 22 22c11 0 21-8 21-22 0-1.2-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.5 4.5 29.5 2 24 2 15.9 2 8.9 6.6 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 46c5.4 0 10.3-2.1 14-5.4l-6.5-5.5C29.7 36.4 27 37 24 37c-5.2 0-9.6-2.6-11.3-6.4l-6.6 5.1C8.7 41.4 15.7 46 24 46z"/>
            <path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.1-2.2 3.9-4 5.1l6.5 5.5C41.2 36 45 30.6 45 24c0-1.2-.1-2.3-.4-3.5z"/>
          </svg>
          <span className="text-white font-black text-base leading-none" style={{ fontFamily: "Archivo" }}>
            {data.rating?.toFixed(1)}
          </span>
          <div className="flex gap-0.5">
            {[0, 1, 2, 3, 4].map((k) => (
              <Star key={k} size={12} fill={k < Math.round(data.rating) ? brand.yellow : "none"} stroke={brand.yellow} strokeWidth={1.5} />
            ))}
          </div>
        </div>
        <p className="text-white/50 text-[11px] uppercase tracking-[0.14em] mt-1.5" style={{ fontFamily: "Space Grotesk" }}>
          {data.userRatingsTotal?.toLocaleString("it-IT")} {t("camp_reviews")}
        </p>

        {/* recensione singola a rotazione */}
        {r && (
          <div key={ri} className="mt-3 pt-3 border-t border-white/10 animate-[fadeIn_0.6s_ease-out]">
            <div className="flex gap-0.5 mb-1.5">
              {[0, 1, 2, 3, 4].map((k) => (
                <Star key={k} size={10} fill={k < r.rating ? brand.yellow : "none"} stroke={brand.yellow} strokeWidth={1.5} />
              ))}
            </div>
            <p className="text-white/85 text-[11px] leading-snug line-clamp-3" style={{ fontFamily: "Inter" }}>
              “{r.text}”
            </p>
            <div className="flex items-center gap-2 mt-2">
              {r.authorPhotoUri && (
                <img src={r.authorPhotoUri} alt={r.author} className="w-5 h-5 rounded-full object-cover" loading="lazy" />
              )}
              <span className="text-white/70 text-[11px] font-medium truncate" style={{ fontFamily: "Space Grotesk" }}>
                {r.author}
              </span>
            </div>
          </div>
        )}
      </div>
    </a>
  );
}


// Ogni slide: immagine pulita (senza testo) + claim sovrapposto via codice (i18n via ui.js: camp_l0..l5).
// L'hashtag #pazziabbastanza è costante; cambia solo il sottotitolo.
const SLIDES = [
  { src: "/campaign/linea-produzione.webp", cap: "camp_l6", pos: "30% 50%", filter: "brightness(0.66) contrast(1.15) saturate(1.1) sepia(0.12)" },
  { src: "/campaign/auto-grafite.webp", cap: "camp_l0" },
  { src: "/campaign/wr6d2s3u_IMG_6627.webp", cap: "camp_l1" },
  { src: "/campaign/aubnaxi4_IMG_6630.webp", cap: "camp_l4" },
];

const MANIFESTO = { manifesto: true, src: "/campaign/linea-produzione.webp", pos: "30% 50%", filter: "brightness(0.38) contrast(1.1) saturate(0.9) sepia(0.12)" };
const paras = () => technology.body.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);

function useMobile() {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}

function Highlight({ text }) {
  return <mark className="px-1.5 py-0.5 rounded-sm font-medium" style={{ background: brand.yellow, color: "#111", WebkitBoxDecorationBreak: "clone", boxDecorationBreak: "clone" }}>{text}</mark>;
}

function ManifestoText({ onMore }) {
  const p = paras();
  return (
    <div data-testid="campaign-manifesto">
      <p className="text-white/90 text-base leading-relaxed" style={{ fontFamily: "Inter" }}>{p[1] || p[0]}</p>
      <p className="mt-4 text-base leading-relaxed" style={{ fontFamily: "Inter" }}><Highlight text={p[p.length - 1]} /></p>
      <button type="button" onClick={onMore} className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white/80 hover:text-white" style={{ fontFamily: "Space Grotesk" }} data-testid="campaign-manifesto-more">
        {t("camp_read_more")} <ArrowRight size={13} />
      </button>
    </div>
  );
}

function ManifestoDesktop({ active }) {
  const p = paras();
  return (
    <div className="absolute z-10 inset-0 flex items-center px-24 lg:px-44 transition-all duration-[900ms] ease-out" style={{ opacity: active ? 1 : 0, transform: active ? "translateY(0)" : "translateY(24px)" }} data-testid="campaign-manifesto-desktop">
      <div className="max-w-[1440px] w-full mx-auto grid grid-cols-12 gap-12 items-center">
        <div className="col-span-5">
          <h2 className="font-black leading-[1.02] text-5xl lg:text-6xl drop-shadow-lg" style={{ fontFamily: "Archivo" }}>
            <span style={{ color: brand.yellow }}>#pazzi</span><span className="text-white">abbastanza</span>
          </h2>
          <p className="mt-4 text-white/95 text-xl lg:text-2xl font-medium" style={{ fontFamily: "Space Grotesk" }}>{t("tech_claim_sub")}</p>
        </div>
        <div className="col-span-7 max-w-[68ch] border-l-2 pl-8 space-y-4 text-white/90 text-base lg:text-lg leading-relaxed whitespace-pre-line" style={{ borderColor: brand.yellow, fontFamily: "Inter" }}>
          {p.map((x, k) => <p key={k}>{k === p.length - 1 ? <Highlight text={x} /> : x}</p>)}
        </div>
      </div>
    </div>
  );
}

function ManifestoDialog({ onClose }) {
  const p = paras();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-h-[88vh] overflow-y-auto bg-black border-white/15 text-white rounded-none p-6" data-testid="campaign-manifesto-dialog">
        <DialogTitle className="font-black text-3xl leading-[1.02]" style={{ fontFamily: "Archivo" }}>
          <span style={{ color: brand.yellow }}>#pazzi</span>abbastanza
        </DialogTitle>
        <p className="-mt-2 text-white/90 text-lg font-medium" style={{ fontFamily: "Space Grotesk" }}>{t("tech_claim_sub")}</p>
        <div className="space-y-4 text-white/90 text-base leading-relaxed whitespace-pre-line" style={{ fontFamily: "Inter" }}>
          {p.map((x, k) => <p key={k}>{k === p.length - 1 ? <Highlight text={x} /> : x}</p>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CampaignSlider() {
  const mobile = useMobile();
  const slides = mobile ? [MANIFESTO, ...SLIDES] : [SLIDES[0], MANIFESTO, ...SLIDES.slice(1)];
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [more, setMore] = useState(false);
  const [hover, setHover] = useState(false);
  const [tx, setTx] = useState(null);
  const n = slides.length;
  const multi = n > 1;
  const go = useCallback((d) => setI((p) => (p + d + n) % n), [n]);
  const user = (fn) => (...a) => { setPaused(true); fn(...a); };

  useEffect(() => { setI(0); }, [mobile]);
  useEffect(() => {
    if (!multi || paused || more || hover) return undefined;
    const tm = setTimeout(() => setI((p) => (p + 1) % n), slides[i]?.manifesto ? (mobile ? 15000 : 20000) : 7000);
    return () => clearTimeout(tm);
  }, [i, n, multi, paused, more, hover, slides, mobile]);

  const onTouchStart = (e) => { setPaused(true); setTx(e.touches[0].clientX); };
  const onTouchEnd = (e) => {
    if (tx == null) return;
    const dx = e.changedTouches[0].clientX - tx;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    setTx(null);
  };

  return (
    <section id="campagna" className="relative w-full bg-black overflow-hidden" data-testid="campaign-slider" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onMouseEnter={() => !mobile && setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="relative w-full" style={{ height: "clamp(480px, 90vh, 980px)" }}>
        {slides.map((slide, idx) => {
          const active = idx === i;
          const lines = slide.cap ? t(slide.cap).split("\n") : [t("tech_claim_sub")];
          return (
            <div
              key={idx}
              className="absolute inset-0 transition-opacity duration-[900ms] ease-out"
              style={{ opacity: active ? 1 : 0, pointerEvents: active ? "auto" : "none" }}
            >
              {/* immagine a tutto schermo (edge-to-edge) con leggero Ken Burns */}
              <img
                src={slide.src}
                alt={`Campagna #pazziabbastanza ${idx + 1}`}
                loading={idx === 0 ? "eager" : "lazy"}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: slide.pos || "50% 50%", filter: slide.filter, transform: active ? "scale(1.06)" : "scale(1)", transition: "transform 9s ease-out" }}
              />
              {/* gradiente per leggibilità testo (scuro in basso a sinistra) */}
              {slide.manifesto && <div className="absolute inset-0 bg-black/50" />}
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 32%, rgba(0,0,0,0) 62%)" }}
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 55%)" }}
              />

              {slide.manifesto && !mobile ? <ManifestoDesktop active={active} /> : (
              <div
                className="absolute z-10 left-0 right-0 bottom-0 px-6 sm:px-24 lg:px-44 pb-24 sm:pb-36 lg:pb-44 transition-all duration-[900ms] ease-out"
                style={{ opacity: active ? 1 : 0, transform: active ? "translateY(0)" : "translateY(24px)" }}
              >
                <div className="max-w-[1440px] mx-auto">
                  {slide.manifesto && <div className="mb-6"><ManifestoText onMore={user(() => setMore(true))} /></div>}
                  <h2 className="font-black leading-[1.02] text-4xl sm:text-5xl lg:text-6xl drop-shadow-lg" style={{ fontFamily: "Archivo" }}>
                    <span style={{ color: brand.yellow }}>#pazzi</span>
                    <span className="text-white">abbastanza</span>
                  </h2>
                  <p className="mt-4 text-white/95 text-lg sm:text-xl lg:text-2xl font-medium max-w-2xl drop-shadow-md" style={{ fontFamily: "Space Grotesk" }}>
                    {lines.map((l, k) => (
                      <React.Fragment key={k}>
                        {k > 0 && <br className="hidden lg:block" />}
                        {k > 0 ? " " : ""}{l}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              </div>
              )}
            </div>
          );
        })}

        {/* overlay recensioni Google in alto a destra */}
        <div className="transition-opacity duration-500" style={{ opacity: slides[i]?.manifesto ? 0 : 1, pointerEvents: slides[i]?.manifesto ? "none" : undefined }} data-testid="campaign-reviews-wrap">
          <ReviewsOverlay />
        </div>

        {/* firma torri.solar sempre in basso a destra */}
        <div className="hidden sm:block absolute z-20 bottom-5 right-6 sm:right-10 lg:right-16">
          <span className="font-black text-lg sm:text-xl tracking-tight drop-shadow-lg" style={{ fontFamily: "Archivo" }}>
            <span className="text-white">torri</span>
            <span style={{ color: brand.yellow }}>.solar</span>
          </span>
        </div>

        {multi && (
          <>
            {!(mobile && slides[i]?.manifesto) && <>
            <button onClick={user(() => go(-1))} data-testid="campaign-prev" aria-label="Precedente" className="absolute z-10 left-4 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-black/40 hover:bg-black/70 text-white transition-colors backdrop-blur rounded-full">
              <ChevronLeft size={24} />
            </button>
            <button onClick={user(() => go(1))} data-testid="campaign-next" aria-label="Successivo" className="absolute z-10 right-4 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-black/40 hover:bg-black/70 text-white transition-colors backdrop-blur rounded-full">
              <ChevronRight size={24} />
            </button>
            </>}

            <div className="absolute z-10 bottom-6 left-6 sm:left-1/2 sm:-translate-x-1/2 flex justify-center gap-2.5" data-testid="campaign-dots">
              {slides.map((_, idx) => (
                <button key={idx} onClick={user(() => setI(idx))} data-testid={`campaign-dot-${idx}`} aria-label={`Vai alla slide ${idx + 1}`} className="tap-xl h-2 rounded-full transition-all" style={{ width: idx === i ? 30 : 8, background: idx === i ? brand.yellow : "rgba(255,255,255,0.45)" }} />
              ))}
            </div>
          </>
        )}
      </div>
      {more && <ManifestoDialog onClose={() => setMore(false)} />}
    </section>
  );
}
